import _ from 'lodash';

import Err from 'models/err';

export class DomainError extends Error {
  constructor(errors, resource) {
    super(`domain error occurred for ${resource}`);
    this.name = 'DomainError';
    this.errors = errors.map(e => new Err(e));
  }

  get message() {
    return this.errors.map(e => e.detail || e.code).join('; ');
  }
}

export class NotExistError extends Error {
  constructor(errors, resource) {
    super(`entity not found for ${resource}`);
    this.name = 'NotExistError';
    this.errors = errors && errors.map(e => new Err(e));
  }

  get message() {
    return (this.errors && this.errors[0].detail) || 'entity not found';
  }
}

export class PermanentError extends Error {
  constructor(errors, resource) {
    super(`permanent error occurred for ${resource}`);
    this.name = 'PermanentError';
    this.errors = errors && errors.map(e => new Err(e));
  }

  get message() {
    return this.errors.map(e => e.detail || e.code).join('; ');
  }
}

export class VersionOutdated extends DomainError {}

export class InfrastructureError extends Error {
  constructor(message) {
    super(message);
    this.name = 'InfrastructureError';
    this.message = message;
  }
}

export class NetworkError extends InfrastructureError {
  constructor(cause) {
    super('network error occurred');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export class RequestTimeout extends InfrastructureError {
  constructor(cause) {
    super('request timed out');
    this.name = 'RequestTimeout';
    this.cause = cause;
  }
}

export class ServerError extends InfrastructureError {
  constructor(response, resource) {
    super(`server error occurred for ${resource}`);
    this.name = 'ServerError';
    this.response = response;
  }
}

export class Unauthorized extends Error {
  constructor(response, resource) {
    const message = `authentication required for ${resource}`;
    super(message);
    this.name = 'Unauthorized';
    this.message = message;
    this.response = response;
  }
}

export function getFriendlyMessage(err) {
  if (err instanceof NetworkError) {
    return `It looks like you're offline. Please check your internet connection and try again.`;
  }

  if (err instanceof RequestTimeout) {
    return `Your last action could not be completed due to a slow or unstable internet connection. Please try again.`;
  }

  return `Hmm. Something seems to have gone wrong. Please refresh your page and try again.`;
}

/**
 * Converts error DTO typically received via a response to a typed error object based on the code of the first
 * error in the `errors` array:
 * - returns NotExistError if the first error has code `not_exist`
 * - returns ServerError if the first error has code `unexpected_error`
 * - returns DomainError otherwise
 * - returns generic Error with error JSON as a message if errorDto doesn't contain `errors` array.
 * @param errorDto - error DTO, expected to contain an object with `errors` array.
 * @returns {NotExistError|ServerError|DomainError|Error}
 */
export function errorFromDto(errorDto) {
  if (_.isArray(errorDto.errors)) {
    if (errorDto.errors[0].code === Err.Code.NOT_EXIST) {
      return new NotExistError(errorDto.errors);
    }

    if (errorDto.errors[0].code === Err.Code.UNEXPECTED_ERROR) {
      return new ServerError();
    }

    return new DomainError(errorDto.errors);
  }

  // Fall back on using error JSON as the error message. It shouldn't happen in a properly designed service.
  return Error(JSON.stringify(errorDto));
}



// WEBPACK FOOTER //
// ./src/scripts/application/lib/error.js